import {
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  AssignmentLateRounded,
  AssignmentTurnedInRounded,
  DeleteRounded,
  EditRounded,
  VisibilityRounded
} from '@material-ui/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { RootState } from '../app/store';
import { useAppDispatch } from '../hooks/useAppRudux';
import { ProjectDocument } from '../pages/ProjectDocument/projectDocumentSlice';
import {
  deleteProjectDocumentRevisionAsync,
  ProjectDocumentRevision,
  ProjectDocumentRevisionWithCurrentWorkflow
} from './../pages/ProjectDocumentRevision/projectDocumentRevisionSlice';
import Card from './Card';
import ConfirmDelete from './ConfirmDelete';
import StatusChip from './StatusChip';
import { PurposeLabel } from '../pages/ProjectDocument/BulkPlanning';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fullList: {
      width: 'auto'
    },
    divider: {
      margin: theme.spacing(1, 0)
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    item55: {
      width: '55%',
      [theme.breakpoints.down('xs')]: {
        width: '100%'
      }
    },
    item45: {
      width: '45%',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        textAlign: 'right'
      }
    }
  })
);

interface ParamProps {
  page: number;
  limit: number;
}

interface DocRevisionCardProps<T> {
  renderDocRevisions: ProjectDocumentRevisionWithCurrentWorkflow[];
  params: T;
  setParams: (params: T) => void;
  document: ProjectDocument;
}

export default function DocRevisionCard<T extends ParamProps>({
  renderDocRevisions,
  params,
  setParams,
  document
}: DocRevisionCardProps<T>) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { user } = useSelector((state: RootState) => state.userProfile);
  const { t } = useTranslation();

  const [openOption, setOpenOption] = useState(false);
  const [selectedDocRevision, setSelectedDocRevision] =
    useState<ProjectDocumentRevision | null>(null);
  const selectedDocPurposeText = selectedDocRevision?.purpose;
  const selectedDocRevisionText = selectedDocRevision?.revision;
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const isDocCreator: boolean = document?.documentCreatorId === user?.id;

  function toggleDrawer(open: boolean, docRevision?: ProjectDocumentRevision) {
    return (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      if (open && docRevision) {
        setSelectedDocRevision(docRevision);
      }
      setOpenOption(open);
    };
  }

  function handleClickDelete() {
    setOpenDelete(!openDelete);
  }

  function handleDeleteDocument() {
    if (selectedDocRevision) {
      const page =
        renderDocRevisions.length === 1
          ? Math.max(0, params.page - 1)
          : params.page;
      dispatch(
        deleteProjectDocumentRevisionAsync({
          projectDocumentRevisionId: selectedDocRevision.id,
          docRevParams: { ...params, page }
        })
      );
      setParams({ ...params, page });
      setOpenDelete(false);
    }
  }

  return (
    <div>
      <ConfirmDelete
        open={openDelete}
        text={`${document.name} - ${selectedDocPurposeText?.abbreviation} rev.${selectedDocRevisionText?.name}`}
        onCloseDelete={handleClickDelete}
        onDelete={handleDeleteDocument}
      />
      <Drawer anchor="bottom" open={openOption} onClose={toggleDrawer(false)}>
        <div
          className={classes.fullList}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <List>
            <ListItem>
              <ListItemText
                primary={`${document.name} - ${selectedDocPurposeText?.abbreviation} rev.${selectedDocRevisionText?.name}`}
              />
            </ListItem>
            <Divider className={classes.divider} />
            <ListItem
              component={NavLink}
              to={`/document-revisions/${selectedDocRevision?.id}`}
              button
            >
              <ListItemIcon>
                <VisibilityRounded />
              </ListItemIcon>
              <ListItemText primary={t('docRevisionCard.viewDocumentRevision') as string} />
            </ListItem>
            <ListItem
              component={NavLink}
              to={`/document-revisions/${selectedDocRevision?.id}/edit`}
              button
            >
              <ListItemIcon>
                <EditRounded />
              </ListItemIcon>
              <ListItemText primary={t('docRevisionCard.editDocumentRevision') as string} />
            </ListItem>
            <Divider className={classes.divider} />
            <ListItem
              button
              disabled={!isDocCreator}
              onClick={handleClickDelete}
            >
              <ListItemIcon>
                <DeleteRounded />
              </ListItemIcon>
              <ListItemText
                primary={t('docRevisionCard.delete') as string}
                secondary={!isDocCreator && t('docRevisionCard.onlyDocumentCreatorCanDelete') as string}
              />
            </ListItem>
          </List>
        </div>
      </Drawer>
      {renderDocRevisions?.map((revision, index) => {
        const [currentWorkflow] = revision.projectDocumentWorkflows;
        return (
          <Card
            key={index}
            to={`/document-revisions/${revision.id}`}
            openDrawer={toggleDrawer(true, revision)}
          >
            <div className={classes.item55}>
              <Typography variant="body1">
                {t('docRevisionCard.purpose')}: {revision.purpose.abbreviation}, {t('docRevisionCard.revision')}:{' '}
                {revision.revision.name}
              </Typography>
              <div className={classes.flexAlignCenter}>
                <Typography variant="subtitle2">
                  {t('docRevisionCard.manHour')}: {revision.manHour}
                </Typography>
                {revision.purpose.id === PurposeLabel.IFR && (
                  <Tooltip
                    title={
                      revision.isAutoApproval
                        ? t('docRevisionCard.autoApprovalWorkflowChecked') as string
                        : t('docRevisionCard.autoApprovalWorkflowUnchecked') as string
                    }
                    arrow
                  >
                    {revision.isAutoApproval ? (
                      <AssignmentTurnedInRounded
                        fontSize="small"
                        color="action"
                      />
                    ) : (
                      <AssignmentLateRounded
                        fontSize="small"
                        color="disabled"
                      />
                    )}
                  </Tooltip>
                )}
              </div>
            </div>
            <div className={classes.item45}>
              <StatusChip
                workflowWithStep={currentWorkflow}
                planIssueDate={revision.planIssueDate}
              />
            </div>
          </Card>
        );
      })}
    </div>
  );
}
